/**
 * Bottom-navigation registry (mobile) — single source of truth for the app's
 * primary navigation, ordered as the logical action flow per role.
 *
 * Scalability: add a role → add a key here; add a destination → one entry.
 * Each tab: { key, label, icon, to, primary?, permission? }
 *   - `primary`    → rendered as the elevated center action (main task for that role).
 *   - `permission` → governor permission key; the tab is hidden when the user lacks it
 *                    (admin bypasses). Keeps the bar consistent without per-screen logic.
 *
 * Order = intuitive sequence: Accueil → main work area → primary action → Notifs → Profil.
 */

import { hasGovernorPermission } from '../hooks/useGovernorPermission.js';

export const NAV_TABS = {
  relais: [
    { key: 'home', label: 'Accueil', icon: 'accueil', to: '/relais/dashboard' },
    { key: 'benef', label: 'Bénéficiaires', icon: 'beneficiaires', to: '/relais/beneficiaires' },
    { key: 'enroll', label: 'Enrôler', icon: 'enroler', to: '/relais/beneficiaires/create', primary: true },
    { key: 'notifs', label: 'Notifs', icon: 'notifications', to: '/notifications' },
    { key: 'profile', label: 'Profil', icon: 'profil', to: '/profile' },
  ],
  parrain_grand: [
    { key: 'home', label: 'Accueil', icon: 'accueil', to: '/parrain/dashboard' },
    { key: 'campaigns', label: 'Campagnes', icon: 'campagnes', to: '/parrain/campagnes' },
    { key: 'ranking', label: 'Classement', icon: 'classement', to: '/parrain/rankings' },
    { key: 'notifs', label: 'Notifs', icon: 'notifications', to: '/notifications' },
    { key: 'profile', label: 'Profil', icon: 'profil', to: '/profile' },
  ],
  parrain_sympa: [
    { key: 'home', label: 'Accueil', icon: 'accueil', to: '/parrain-sympa/dashboard' },
    { key: 'ranking', label: 'Classement', icon: 'classement', to: '/parrain-sympa/rankings' },
    { key: 'notifs', label: 'Notifs', icon: 'notifications', to: '/notifications' },
    { key: 'profile', label: 'Profil', icon: 'profil', to: '/profile' },
  ],
  gouverneur: [
    { key: 'home', label: 'Accueil', icon: 'accueil', to: '/gouverneur/dashboard' },
    { key: 'validation', label: 'Validation', icon: 'validation', to: '/gouverneur/validation', permission: 'validate_users' },
    { key: 'reports', label: 'Rapports', icon: 'rapports', to: '/rapports/classement', permission: 'view_reports' },
    { key: 'notifs', label: 'Notifs', icon: 'notifications', to: '/notifications' },
    { key: 'profile', label: 'Profil', icon: 'profil', to: '/profile' },
  ],
  admin: [
    { key: 'home', label: 'Accueil', icon: 'accueil', to: '/gouverneur/dashboard' },
    { key: 'users', label: 'Utilisateurs', icon: 'utilisateurs', to: '/admin/users' },
    { key: 'reports', label: 'Rapports', icon: 'rapports', to: '/rapports/classement' },
    { key: 'notifs', label: 'Notifs', icon: 'notifications', to: '/notifications' },
    { key: 'profile', label: 'Profil', icon: 'profil', to: '/profile' },
  ],
  beneficiaire: [
    { key: 'home', label: 'Accueil', icon: 'accueil', to: '/dashboard' },
    { key: 'aide', label: 'Aide', icon: 'aide', to: '/beneficiaire/accompagnement', primary: true },
    { key: 'notifs', label: 'Notifs', icon: 'notifications', to: '/notifications' },
    { key: 'profile', label: 'Profil', icon: 'profil', to: '/profile' },
  ],
};

/** Tabs for the current user, permission-filtered. Empty array when N/A. */
export function getNavTabs(user) {
  if (!user?.type) return [];
  const tabs = NAV_TABS[user.type] || [];
  return tabs.filter((t) => !t.permission || hasGovernorPermission(user, t.permission));
}

/** Role home / Accueil path — used by logo + dashboard entry (avoids public `/` flash). */
export function getRoleHomePath(user) {
  if (!user?.type) return '/';
  const home = (NAV_TABS[user.type] || []).find((t) => t.key === 'home');
  return home?.to || '/dashboard';
}

/** Active tab key for a pathname — longest matching `to` wins (so /…/create beats /…). */
export function activeTabKey(tabs, pathname) {
  let best = null;
  let bestLen = -1;
  for (const t of tabs) {
    const match = pathname === t.to || pathname.startsWith(`${t.to}/`);
    if (match && t.to.length > bestLen) {
      best = t.key;
      bestLen = t.to.length;
    }
  }
  return best;
}
